// Video Director — the "director" tab of VideoModal (spec decisions 8, 15, 16; §9).
// One project doc lives on the middleware (/api/video-director/*). This file
// owns every API call, the 3 s doc poll, and steps ① Brief ② Treatment
// ③ Storyboard ⑤ Finish; step ④ is DirectorEditStep. All markup reuses
// VideoModal / VideoAIGenerateTab / VideoStoryboardPanel classes. Nothing here
// opens a popup: a credit refusal goes to VideoModal's InsufficientBalanceModal
// through onInsufficientBalance; server error text is never shown raw.

import React, { useCallback, useEffect, useRef, useState } from "react";
import i18n from "i18next";
import {
  LuBookOpen,
  LuCheck,
  LuClapperboard,
  LuFolderOpen,
  LuChevronDown,
  LuFilePlus,
  LuHandCoins,
  LuGraduationCap,
  LuLightbulb,
  LuMap,
  LuPenLine,
  LuRefreshCw,
  LuTrash2,
} from "react-icons/lu";
import { getToken } from "../../../../../common/environment_config.es6";
import DirectorEditStep from "./DirectorEditStep";
import DirectorFinish from "./DirectorFinish";
import {
  DRAFT_MODEL,
  defaultFinalModel,
  FinalizeStrip,
  LdBottom,
  LdTop,
  StartOptions,
  draftShotIds,
  ldDraftOn,
} from "./DirectorLd";
import StoryAssetsPanel, { CookbookControl, CookbookSection } from "./DirectorStory";
import { PreviewHost, PreviewThumb } from "./ImagePreview";
import {
  ActionMenu,
  ErrorAlert,
  BriefModelRow,
  ModelPick,
  PolicyChip,
  effectiveModel,
  priced,
  useFilePick,
} from "./DirectorPanels";
import LibraryPicker from "./LibraryPicker";
import { isVideoUrl, styleThumb } from "../videoStyleThumbs";
import { relativeTime } from "../../../../utils/relativeTime";
import { getUndoRedoIntent, isContentEditableFocus } from "../../../../utils/undoRedoShortcut";
import { tabInDialog } from "../../../../utils/tabInDialog";
import {
  edlSignature,
  emptyHistory,
  hasPendingWork,
  isContinueShot,
  isStale,
  locate,
  newSegId,
  pushHistory,
  redo,
  segmentStart,
  splitAt,
  undo,
} from "./edl";

const POLL_MS = 3000; // common/job_poll.es6 cadence
const MAX_NARRATION_WORDS = 300; // spec §10
// VideoAIGenerateTab's ASPECT_RATIOS limited to DEFAULT_ASPECT_RATIO_IDS (Veo: 16:9 | 9:16).
const ASPECTS = [
  { id: "16:9", w: 16, h: 9 },
  { id: "9:16", w: 9, h: 16 },
];
const STEP_BY_STATUS = {
  prefilling: "brief",
  brief: "brief",
  treating: "treatment",
  treatment: "treatment",
  storyboard: "storyboard",
  editing: "edit",
};
const STEPS = ["brief", "treatment", "storyboard", "edit", "finish"];
const STEP_KEYS = {
  brief: "Brief",
  treatment: "Treatment",
  storyboard: "Storyboard",
  edit: "Edit",
  finish: "Finish",
};
// VideoStoryboardPanel's STATUS_BG / STATUS_COLOR for pending / completed / failed.
const BADGE = {
  pending: ["#fffbeb", "#f59e0b"],
  ready: ["#f0fdf4", "#15803d"],
  failed: ["#fef2f2", "#ef4444"],
};
const STATUS_LABEL = {
  pending: "StatusGenerating",
  ready: "StatusCompleted",
  failed: "StatusFailed",
};
const BRIEF_NOTES = ["cast", "locations", "criticalActions", "brandPolicy"];
const MAX_SCENARIO = 8000; // server LIMITS.scenarioChars
const MAX_CAST_PHOTOS = 4; // server LIMITS.castPhotos

const t = (key, opts) => i18n.t(`FileModal.APIProviders.VideoGeneration.Director.${key}`, opts);
const tl = (key, opts) => t(`Ld.${key}`, opts);
const tv = (key) => i18n.t(`FileModal.APIProviders.VideoGeneration.${key}`);
// A pending item past edl.js STALE_MS counts as failed (its worker is gone).
const statusOf = (item) => (isStale(item) ? "failed" : item.status);
// 400 codes the UI maps to Errors.* (never raw server text).
const CODE_400 = {
  "continue.next_required": "next_required",
  "edl.full": "edl_full",
  "project.limit": "project_limit",
  "import.source_invalid": "import_source_invalid",
  "continue.source_gone": "continue_source_gone",
  "end_frame.none": "end_frame_none",
  "end_frame.invalid": "end_frame_invalid",
  "end_frame.requires_start": "end_frame_requires_start",
  "shot.not_regenerable": "not_regenerable",
  "refs.full": "refs_full",
  "refs.source_invalid": "refs_source_invalid",
  "refs.source_required": "refs_source_required",
  "refs.invalid": "refs_invalid",
  "keyframe.not_from_ref": "keyframe_not_from_ref",
  "shot.invalid_cast": "shot_invalid_cast",
  "shot.invalid_location": "shot_invalid_location",
  "cast_photos.consent_required": "cast_photos_consent_required",
  "cast_photos.invalid": "cast_photos_invalid",
  "finalize.no_draft": "finalize_no_draft",
};
// Model pickers read the per-model caps from `models`.
const withCaps = (res) =>
  res?.videoCaps ? { ...res, models: { ...res.models, videoCaps: res.videoCaps } } : res;
const errorText = (code) =>
  t(code === "content_filtered" ? "Errors.content_filtered" : "Errors.generic");

// VideoStoryboardPanel's input look.
const inputStyle = {
  width: "100%",
  padding: "8px 12px",
  fontSize: "13px",
  border: "1px solid #e2e8f0",
  borderRadius: "6px",
  background: "#fff",
  color: "#1f2937",
  transition: "border-color 0.2s, box-shadow 0.2s",
  lineHeight: "1.5",
  resize: "vertical",
};
const labelStyle = { fontSize: "13px", fontWeight: 600, color: "#334155", marginBottom: "6px" };
const hintStyle = {
  marginTop: "6px",
  padding: "6px 10px",
  background: "#fef2f2",
  border: "1px solid #fecaca",
  borderRadius: "4px",
  fontSize: "12px",
  color: "#991b1b",
};
// Required marker beside a label: a muted asterisk, warning-coloured once a Next attempt found the field missing.
const Mark = ({ missing }) => (
  <span
    aria-hidden="true"
    title={t("Required")}
    data-vd-missing={missing ? "" : undefined}
    style={{ marginLeft: 3, color: missing ? "#b45309" : "#94a3b8", fontWeight: missing ? 700 : 400 }}
  >
    *
  </span>
);
const missingBorder = { borderColor: "#d97706" };
const focusFirstMissing = () => {
  const el =
    document.querySelector('[aria-invalid="true"]') || document.querySelector("[data-vd-missing]");
  el?.scrollIntoView?.({ block: "center" });
  el?.focus?.();
};
const smallBtn = {
  fontSize: 12,
  padding: "4px 12px",
  display: "flex",
  alignItems: "center",
  gap: "4px",
};

export const wordCount = (text) =>
  (
    String(text || "")
      .trim()
      .match(/\S+/g) || []
  ).length;

// Server briefFieldFilled: a style is filled by a styleKey or a non-blank custom style.
const filled = (brief, key) =>
  key === "styleKey" || key === "customStyle"
    ? !!(brief.styleKey || String(brief.customStyle ?? "").trim())
    : !!String(brief[key] ?? "").trim();

/**
 * Brief fields that block "Next": server-flagged required ones left empty (narration is optional in "My own idea",
 * which needs a scenario instead), narration over 300 words, length outside 10..120 s, and in "From my photos"
 * at least one named photo whose ref (in `refs`) is ready and carries the likeness consent.
 */
// The L&D form shows only when the server flow is ld AND the brief carries its ld block; the chip reads the same.
const isLdForm = (project, brief) => project.flow === "ld" && !!brief?.ld;

export function briefIssues(brief, refs = []) {
  const ownIdea = brief.startMode === "prompt";
  const issues = (brief.required || []).filter(
    (key) => !(ownIdea && key === "narration") && !filled(brief, key)
  );
  if (ownIdea && !filled(brief, "scenario")) issues.push("scenario");
  if (brief.castMode === "photos") {
    const photos = brief.castPhotos || [];
    if (
      !photos.length ||
      photos.some(
        (c) => !String(c.name ?? "").trim() || !refs.some((r) => r.id === c.refId && r.status === "ready" && r.consent)
      )
    )
      issues.push("castPhotos");
  }
  if (brief.ld && !String(brief.ld.objective ?? "").trim()) issues.push("ld.objective"); // L&D flow only
  if (wordCount(brief.narration) > MAX_NARRATION_WORDS && !issues.includes("narration")) {
    issues.push("narration");
  }
  if (!(Number.isInteger(brief.targetSec) && brief.targetSec >= 10 && brief.targetSec <= 120)) {
    issues.push("targetSec");
  }
  return issues;
}

// Key-order-insensitive: Postgres jsonb reorders object keys, so a JSON.stringify compare would lie.
const sameEdl = (a, b) =>
  !!a &&
  !!b &&
  a.length === b.length &&
  a.every(
    (x, i) =>
      x.id === b[i].id &&
      x.shotId === b[i].shotId &&
      x.takeId === b[i].takeId &&
      x.inFrame === b[i].inFrame &&
      x.outFrame === b[i].outFrame
  );

const member = ({ id, name, description }) => ({ id, name, description });

/** What the client may write while status === "treatment" (spec §4 field ownership). */
export function treatmentPatch(draft) {
  return {
    bible: {
      logline: draft.logline,
      look: draft.look,
      cast: draft.cast.map(member),
      locations: draft.locations.map(member),
    },
    shots: draft.shots.map(
      ({ id, narration, durationSec, camera, prompt, castIds, locationId, keyframe }) => ({
        id,
        narration,
        durationSec,
        camera,
        prompt,
        castIds,
        locationId,
        keyframe: { prompt: keyframe.prompt },
      })
    ),
  };
}

/** Storyboard / editing write: per shot prompt, keyframe.prompt, keyframe.approved (spec §4). */
export function approvalPatch(shots, approve) {
  return {
    shots: shots.map((s) => ({
      id: s.id,
      prompt: s.prompt,
      keyframe: { prompt: s.keyframe?.prompt, approved: !!approve(s) },
    })),
  };
}

const toTreatmentDraft = (project) => ({
  logline: project.bible.logline,
  look: project.bible.look,
  cast: project.bible.cast.map(member),
  locations: project.bible.locations.map(member),
  shots: project.shots.map((s) => ({ ...s, keyframe: { ...s.keyframe } })),
});

function Field({
  label,
  value,
  onChange,
  multiline,
  rows = 3,
  missing,
  required,
  note,
  children,
  ...input
}) {
  const props = {
    className: "add-video-modal__scene-prompt-textarea",
    style: missing ? { ...inputStyle, ...missingBorder } : inputStyle,
    "aria-required": required || undefined,
    "aria-invalid": missing || undefined,
    value: value ?? "",
    "aria-label": label,
    onChange: (e) => onChange(e.target.value),
    ...input,
  };
  return (
    <div style={{ marginBottom: 12 }}>
      <div className="add-video-modal__scene-field-label" style={labelStyle}>
        {label}
        {(required || missing) && <Mark missing={missing} />}
        {note && <span style={{ marginLeft: 8, fontWeight: 400, fontSize: 12, color: "#b45309" }}>{note}</span>}
      </div>
      {multiline ? (
        <textarea rows={rows} maxLength={2000} {...props} />
      ) : (
        <input maxLength={2000} {...props} />
      )}
      {children}
    </div>
  );
}

// VideoStoryboardPanel's "creating" card.
const Processing = ({ label, note }) => (
  <div
    role="status"
    className="add-video-modal__status-card add-video-modal__status-card--processing"
  >
    <div className="add-video-modal__status-title">
      <span className="add-video-modal__spinner" />
      {label}
    </div>
    {note && <div style={{ fontSize: 13, color: "#64748b", marginTop: 6 }}>{note}</div>}
  </div>
);

const skeletonBar = { height: 14, borderRadius: 4, background: "#e2e8f0", marginTop: 8 };

// The lightbox collection of a card list: [{ still, name, description }] -> (entry) => preview prop.
const gallery = (entries) => {
  const ready = entries.filter((e) => e.still && statusOf(e.still) === "ready" && e.still.url);
  const items = ready.map((e) => ({ url: e.still.url, name: e.name, description: e.description }));
  return (entry) => ({ items, index: ready.indexOf(entry) });
};

// Contain, never crop: a cast still is a composite character sheet.
function Frame({ still, preview }) {
  const status = still ? statusOf(still) : null;
  const ready = status === "ready" && still.url;
  return (
    <>
      <PreviewThumb
        preview={ready ? preview : null}
        clickable
        className="add-video-modal__vd-pv-wrap--fill"
      >
        <div className="add-video-modal__vd-img">
          {ready && <img src={still.url} alt="" style={{ objectFit: "contain" }} />}
          {status === "pending" && <span className="add-video-modal__scene-spinner" />}
          {status === "failed" && (
            <span className="add-video-modal__vd-note">{errorText(still.error)}</span>
          )}
        </div>
      </PreviewThumb>
      <PolicyChip item={still} />
    </>
  );
}

// A content-filtered still whose server offered a stricter rewrite.
const saferOf = (still) =>
  still?.error === "content_filtered" && statusOf(still) === "failed" && still.suggestedPrompt;

function SaferButton({ onClick }) {
  return (
    <button
      type="button"
      className="add-video-modal__btn add-video-modal__btn--secondary"
      style={smallBtn}
      onClick={onClick}
    >
      {t("UseSaferPrompt")}
    </button>
  );
}

function Badge({ status, label }) {
  const [bg, color] = BADGE[status] || BADGE.pending;
  return (
    <span className="add-video-modal__scene-badge" style={{ background: bg, color }}>
      {status === "pending" && <span className="add-video-modal__scene-spinner" />}
      {label ?? tv(STATUS_LABEL[status] || "StatusPending")}
    </span>
  );
}

const Pencil = ({ label, onClick }) => (
  <button
    type="button"
    className="add-video-modal__vd-pencil"
    aria-label={`${tv("Edit")} ${label}`}
    title={tv("Edit")}
    onClick={onClick}
  >
    <LuPenLine size={12} />
  </button>
);

// Right-side panel inside the popup body (no popup over popup); Esc closes only this.
function Drawer({ title, saveLabel, saveDisabled, onSave, onCancel, children }) {
  const ref = useRef(null);
  useEffect(() => {
    ref.current?.focus();
  }, []);
  return (
    <div
      ref={ref}
      tabIndex={-1}
      role="complementary"
      className="add-video-modal__vd-drawer"
      onKeyDown={(e) => {
        if (e.key === "Escape") {
          e.stopPropagation();
          onCancel();
        }
      }}
    >
      <div className="add-video-modal__vd-drawer-title">{title}</div>
      <div className="add-video-modal__vd-drawer-body">{children}</div>
      <div className="add-video-modal__vd-drawer-footer">
        <button type="button" className="app-modal__btn app-modal__btn--ghost" onClick={onCancel}>
          {tv("Cancel")}
        </button>
        <button
          type="button"
          className="app-modal__btn app-modal__btn--primary"
          disabled={saveDisabled}
          onClick={onSave}
        >
          {saveLabel}
        </button>
      </div>
    </div>
  );
}

// VideoModal's credit button + popover (VideoModal.jsx ~2336-2538), fed by
// GET /video-director/credit unit prices × this step's counts.
function CreditEstimate({ btnRef, loading, open, rows, available, onToggle }) {
  const total = Number(rows.reduce((sum, r) => sum + r.cost, 0).toFixed(4));
  const sufficient = available >= total;
  const amount = (value, strong) => (
    <span style={{ fontWeight: strong ? 700 : 600, color: "#1e293b", fontSize: "13px" }}>
      {value.toFixed(2)}&nbsp;
      <span style={{ color: "#8b95af", fontWeight: 400 }}>{i18n.t("ImageHistory.Credits")}</span>
    </span>
  );
  return (
    <>
      <style>{`@keyframes vid-credit-spin{from{transform:rotate(0deg)}to{transform:rotate(360deg)}}`}</style>
      <div ref={btnRef} style={{ position: "relative", order: -1, marginRight: "auto" }}>
        <button
          type="button"
          onClick={onToggle}
          disabled={loading}
          title={i18n.t("CreditEstimate.Tooltip")}
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: "6px",
            padding: "9px 16px",
            borderRadius: "12px",
            border: `1px solid ${open ? "#c4b5fd" : "#dce3f3"}`,
            background: open ? "#eef2ff" : "#f8fafc",
            color: open ? "#7c3aed" : "#64748b",
            fontSize: "14px",
            fontWeight: 600,
            cursor: loading ? "wait" : "pointer",
            transition: "background 0.18s ease, color 0.18s ease, border-color 0.18s ease",
            outline: "none",
            whiteSpace: "nowrap",
          }}
        >
          {loading ? (
            <LuRefreshCw size={14} style={{ animation: "vid-credit-spin 0.8s linear infinite" }} />
          ) : (
            <LuHandCoins size={15} style={{ color: "#f59e0b" }} />
          )}
          <span>
            {loading ? i18n.t("CreditEstimate.Loading") : i18n.t("CreditEstimate.Button")}
          </span>
        </button>
        {open && (
          <div
            style={{
              position: "absolute",
              bottom: "calc(100% + 10px)",
              left: 0,
              background: "#ffffff",
              borderRadius: "12px",
              border: "1px solid #e2e8f0",
              padding: "16px 20px",
              minWidth: "240px",
              boxShadow: "0 4px 24px rgba(15, 23, 42, 0.12)",
              zIndex: 9999,
              fontSize: "13px",
              lineHeight: 1.6,
            }}
          >
            <div
              style={{
                position: "absolute",
                bottom: -7,
                left: 20,
                width: 13,
                height: 13,
                background: "#ffffff",
                border: "1px solid #e2e8f0",
                borderTop: "none",
                borderLeft: "none",
                transform: "rotate(45deg)",
              }}
            />
            <div
              style={{
                fontWeight: 700,
                marginBottom: 12,
                fontSize: "14px",
                color: "#1e293b",
                display: "flex",
                alignItems: "center",
                gap: 7,
              }}
            >
              <LuHandCoins size={15} style={{ color: "#f59e0b" }} />
              {i18n.t("CreditEstimate.Title")}
            </div>
            {rows.map((row) => (
              <div
                key={row.label}
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  marginBottom: 6,
                }}
              >
                <span style={{ color: "#64748b", fontSize: "13px" }}>{row.label}</span>
                {amount(row.cost)}
              </div>
            ))}
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginTop: 4,
                paddingTop: 8,
                borderTop: "1px solid #f1f5f9",
                marginBottom: 14,
                paddingBottom: 14,
                borderBottom: "1px solid #f1f5f9",
              }}
            >
              <span style={{ color: "#64748b", fontWeight: 600 }}>
                {i18n.t("CreditEstimate.Cost")}
              </span>
              {amount(total, true)}
            </div>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: 14,
                paddingBottom: 14,
                borderBottom: "1px solid #f1f5f9",
              }}
            >
              <span style={{ color: "#64748b" }}>{i18n.t("CreditEstimate.Balance")}</span>
              {amount(available, true)}
            </div>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 6,
                padding: "7px 12px",
                borderRadius: 8,
                background: sufficient ? "rgba(124,58,237,0.08)" : "rgba(239,68,68,0.08)",
                color: sufficient ? "#7c3aed" : "#ef4444",
                fontWeight: 600,
                fontSize: "12px",
              }}
            >
              {sufficient
                ? `✓ ${i18n.t("CreditEstimate.Sufficient")}`
                : `✗ ${i18n.t("CreditEstimate.Insufficient")}`}
            </div>
          </div>
        )}
      </div>
    </>
  );
}

export default function VideoDirector({
  apiUrl = "",
  trainingId,
  navId,
  projectId,
  importUrl,
  importPromptProfile,
  direct,
  videoStyles = [],
  languageOptions = [],
  onInsufficientBalance,
  onAddToScene,
  canReplace,
  audioUrl,
  sceneNarrationText,
  uploadFunction,
  onOpenLibrary,
}) {
  const hasText = !!sceneNarrationText?.trim(); // the scene has narration text (generated audio is not required)
  const [project, setProject] = useState(undefined); // undefined = loading · null = none for this scene
  const [drafts, setDrafts] = useState(null); // null = closed · { items: undefined | [] } = the drafts list
  const [missing, setMissing] = useState(false);
  const [error, setError] = useState(null); // Director.Errors.* key
  const [reused, setReused] = useState(false); // the last create returned an earlier project (unchanged narration): false, or that project's status (cleared once the step moves on)
  const [busy, setBusy] = useState(false);
  const [tick, setTick] = useState(0);
  const [view, setView] = useState(null); // "storyboard" | "finish" while status === "editing"
  useEffect(() => {
    if (reused && (view || project?.status !== reused)) setReused(false);
  }, [reused, view, project?.status]);
  const [history, setHistory] = useState(emptyHistory);
  const [briefDraft, setBriefDraft] = useState(null);
  const [briefTried, setBriefTried] = useState(false); // Next clicked while invalid: only then highlight the missing fields
  const [switchingTo, setSwitchingTo] = useState(null); // flow chip whose PATCH is in flight
  const [likenessOk, setLikenessOk] = useState(false); // "From my photos": permission ticked this session
  const [treatDraft, setTreatDraft] = useState(null);
  const [drawer, setDrawer] = useState(null); // { kind, i, v } — the open edit drawer
  const [storyOpen, setStoryOpen] = useState(false); // the Story & assets overlay
  const [newMenu, setNewMenu] = useState(null); // { x, y } — the header "New video" menu
  const [cookbook, setCookbook] = useState(null); // the course cookbook { cast, locations } (shared by every video of this training)
  const [credit, setCredit] = useState(null);
  const [creditOpen, setCreditOpen] = useState(false);
  const [creditLoading, setCreditLoading] = useState(false);
  const [videoPick, setVideoModel] = useState(null); // Generate takes (storyboard step); null = the brief's pick
  const videoModel = videoPick ?? project?.brief?.videoModel ?? "veo-fast";
  const [imageModel, setImageModel] = useState("gemini-nano"); // keyframe regenerate
  const [lookOpen, setLookOpen] = useState(false); // L&D brief: the style picker behind the Look row
  const [finalPick, setFinalModel] = useState(null); // L&D: model the drafts are finalized on (null = default)
  const finalModel = finalPick ?? defaultFinalModel(credit?.models, project?.brief?.videoModel);
  const [finalizeOpen, setFinalizeOpen] = useState(false);
  const creditBtnRef = useRef(null);
  const aliveRef = useRef(true);
  const projectRef = useRef(null);
  const mutatingRef = useRef(0);
  const addsInFlight = useRef(0); // ref-adding jobs in flight
  const importRef = useRef(null);
  const directRef = useRef(null);
  const [showAdvanced, setShowAdvanced] = useState(false); // simple (animate) mode: reveal the full editor for this session
  const [autoShot, setAutoShot] = useState(null); // { keyframeRefId } — the Edit step opens Add shot with it once
  const [pickPhoto, photoInput] = useFilePick(async (file) => {
    const refId = await uploadRef(file, { likenessConsent: true });
    if (!refId || !aliveRef.current) return;
    setBriefDraft(
      (b) =>
        b &&
        !(b.castPhotos || []).some((c) => c.refId === refId) && {
          ...b,
          castPhotos: [...(b.castPhotos || []), { refId, name: "", role: "" }].slice(
            0,
            MAX_CAST_PHOTOS
          ),
        }
    );
  });
  const sentEdlRef = useRef(null); // the EDL this client last PATCHed
  const balanceRef = useRef(onInsufficientBalance);
  balanceRef.current = onInsufficientBalance; // the parent's handler may change every render
  const tid = trainingId || window.idealStudioTrainingId;

  // Raw fetch + bearer token, exactly like VideoModal's own calls.
  const api = useCallback(
    async (method, path, body) => {
      const res = await fetch(`${apiUrl}api/video-director/${path}`, {
        method,
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${getToken()}` },
        body: body === undefined ? undefined : JSON.stringify(body),
      });
      // Endpoint 1 answers an EMPTY 200 body when the scene has no project yet.
      const text = await res.text();
      let data = null;
      try {
        data = text ? JSON.parse(text) : null;
      } catch {
        data = null;
      }
      if (!res.ok) {
        const err = new Error(`HTTP ${res.status}`);
        err.status = res.status;
        err.data = data || {};
        throw err;
      }
      return data;
    },
    [apiUrl]
  );

  // Refetched when the status moves: a treatment's new cast / locations join the cookbook as it lands.
  useEffect(() => {
    if (!project?.id || !tid) return;
    api("GET", `cookbook?trainingId=${encodeURIComponent(tid)}`)
      .then((c) => aliveRef.current && setCookbook(c))
      .catch(() => {
        /* the section just stays hidden */
      });
  }, [api, tid, project?.id, project?.status]);

  const show = useCallback((doc) => {
    // A render became ready (or an import landed one): tell the library to refetch.
    if (doc?.render?.status === "ready" && projectRef.current?.render?.status !== "ready") {
      window.dispatchEvent(new Event("aidea:libraryChanged"));
    }
    projectRef.current = doc;
    setProject(doc);
  }, []);

  // A response older than what is on screen (a poll that started before a PATCH landed) is dropped.
  const applyDoc = useCallback(
    (doc) => {
      if (!aliveRef.current || !doc) return;
      const cur = projectRef.current;
      if (cur && cur.id === doc.id && doc.version < cur.version) return;
      // A foreign EDL change (worker-inserted segment) outdates every undo snapshot; our own echo does not.
      if (cur && !sameEdl(cur.edl, doc.edl) && !sameEdl(sentEdlRef.current, doc.edl)) {
        setHistory(emptyHistory);
      }
      show(doc);
    },
    [show]
  );

  const fail = useCallback(
    (err) => {
      if (!aliveRef.current) return;
      if (balanceRef.current?.(err?.data)) return;
      if (err?.status === 404 && err?.data?.message === "project.not_found") {
        show(null);
        setMissing(true);
        return;
      }
      const code = err?.data?.message;
      const conflict = err?.status === 409 && code === "version.conflict";
      setError(
        conflict
          ? "conflict"
          : err?.status === 409 && code === "busy"
            ? "busy"
            : (err?.status === 400 && CODE_400[code]) || "generic"
      );
    },
    [show]
  );

  const refresh = useCallback(async () => {
    const id = projectRef.current?.id;
    if (!id) return;
    try {
      const doc = await api("GET", `projects/${id}`);
      // A PATCH in flight owns the screen (its optimistic EDL); its response brings the newer doc.
      if (mutatingRef.current === 0) applyDoc(doc);
    } catch (err) {
      if (err?.status === 404) fail(err); // network blips: the next tick retries
    } finally {
      if (aliveRef.current) setTick((n) => n + 1);
    }
  }, [api, applyDoc, fail]);

  // Initial load: the reopened box's project, else an import of the box's video, else the newest one for this scene.
  useEffect(() => {
    aliveRef.current = true;
    const path = projectId
      ? `projects/${encodeURIComponent(projectId)}`
      : `projects?trainingId=${encodeURIComponent(tid || "")}&navId=${encodeURIComponent(navId || "")}`;
    // direct = { latest } | { blank, imageUrl?, aspect? }: { latest } opens this scene's newest project (null = empty
    // state, nothing is created just by opening); { blank } creates a blank one now (optionally with a library image
    // already in its reference set). One run per mount, like the import.
    const openDirect = async () => {
      if (direct.latest) return api("GET", path);
      const res = await api("POST", "projects/blank", {
        trainingId: tid,
        navId,
        ...(direct.aspect ? { aspect: direct.aspect } : {}),
      });
      const doc = res.project || res;
      if (!direct.imageUrl) return doc;
      try {
        const up = await api("POST", `projects/${doc.id}/refs/upload`, {
          version: doc.version,
          url: direct.imageUrl,
        });
        if (up?.refId && aliveRef.current) setAutoShot({ keyframeRefId: up.refId });
        return await api("GET", `projects/${doc.id}`);
      } catch (err) {
        fail(err);
        return doc;
      }
    };
    (!projectId && importUrl
      ? // One import POST per mount, however often this effect re-runs. {project, jobId:null} = existing import, just show it.
        (importRef.current ||= api("POST", "projects/import", {
          trainingId: tid,
          navId,
          url: importUrl,
          ...(importPromptProfile ? { promptProfile: importPromptProfile } : {}),
        }).then((res) => res.project))
      : !projectId && direct
        ? (directRef.current ||= openDirect())
        : api("GET", path)
    )
      .then((doc) => {
        if (aliveRef.current) show(doc || null);
      })
      .catch((err) => {
        fail(err);
        if (aliveRef.current && err?.status !== 404) show(null); // no endless "Loading…"
      });
    return () => {
      aliveRef.current = false;
    };
  }, [api, projectId, importUrl, importPromptProfile, direct, tid, navId, show, fail]);

  // Poll the doc every 3 s while a worker owes something (spec decision 8); dies with the component.
  useEffect(() => {
    if (!hasPendingWork(project)) return undefined;
    const timer = setTimeout(refresh, POLL_MS);
    return () => clearTimeout(timer);
  }, [project, tick, refresh]);

  useEffect(() => {
    // an editing project can revisit its Brief / Treatment (read-only) through "Starting point"
    const looking = project?.status === "editing" ? view : null;
    if ((project?.status === "brief" || looking === "brief") && !briefDraft)
      setBriefDraft({
        ...project.brief,
        // the L&D flow has no start-from choice: its objective is the story source
        ...(project.flow === "ld"
          ? {}
          : {
              startMode:
                project.brief.startMode ?? (project.brief.narration?.trim() ? "scene" : "prompt"),
            }),
      });
    if (
      (project?.status === "treatment" || looking === "treatment") &&
      project.bible &&
      !treatDraft
    ) {
      setTreatDraft(toTreatmentDraft(project));
    }
  }, [project, briefDraft, treatDraft, view]);

  // Model prices (credit.models) for the pickers of the storyboard and edit steps.
  const needsPrices =
    project?.status === "storyboard" ||
    project?.status === "editing" ||
    project?.status === "brief"; // the brief's AI model row (and the L&D cost estimate)
  useEffect(() => {
    if (!needsPrices || credit) return;
    api("GET", "credit")
      .then((res) => aliveRef.current && setCredit((cur) => cur || withCaps(res)))
      .catch(() => {}); // pickers just show no price; the credit popover reports errors
  }, [needsPrices, credit, api]);

  // VideoModal: close the credit popover on an outside click.
  useEffect(() => {
    if (!creditOpen) return undefined;
    const onClickOutside = (e) => {
      if (creditBtnRef.current && !creditBtnRef.current.contains(e.target)) setCreditOpen(false);
    };
    document.addEventListener("mousedown", onClickOutside);
    return () => document.removeEventListener("mousedown", onClickOutside);
  }, [creditOpen]);

  /** POST (or DELETE) a job endpoint with the doc version; one refetch + resend on version.conflict. */
  const runJob = useCallback(
    async (path, body = {}, method = "POST") => {
      const id = projectRef.current?.id;
      if (!id) return false;
      setBusy(true);
      setError(null);
      try {
        const send = () =>
          method === "DELETE"
            ? api("DELETE", `projects/${id}/${path}?version=${projectRef.current.version}`)
            : api("POST", `projects/${id}/${path}`, {
                ...body,
                version: projectRef.current.version,
              });
        let res;
        try {
          res = await send();
        } catch (err) {
          if (err?.status !== 409 || err?.data?.message !== "version.conflict") throw err;
          applyDoc(await api("GET", `projects/${id}`));
          res = await send();
        }
        await refresh();
        return res || true;
      } catch (err) {
        fail(err);
        return false;
      } finally {
        if (aliveRef.current) setBusy(false);
      }
    },
    [api, applyDoc, fail, refresh]
  );

  /**
   * PATCH `fields` with the doc version. On version.conflict: GET the fresh doc,
   * rebuild the fields with replay(fresh) and send once more; replay → null means
   * the change no longer applies (show "conflict", send nothing). Any failure
   * reloads the server doc so an optimistic EDL never outlives a rejected save.
   */
  const mutate = useCallback(
    async (fields, replay) => {
      const id = projectRef.current?.id;
      if (!id) return null;
      const path = `projects/${id}`;
      mutatingRef.current += 1;
      setBusy(true);
      setError(null);
      try {
        let doc;
        try {
          sentEdlRef.current = fields.edl || null;
          doc = await api("PATCH", path, { ...fields, version: projectRef.current.version });
        } catch (err) {
          if (err?.status !== 409 || err?.data?.message !== "version.conflict") throw err;
          const fresh = await api("GET", path);
          const again = replay(fresh);
          if (!again) {
            applyDoc(fresh);
            setError("conflict");
            return null;
          }
          sentEdlRef.current = again.edl || null;
          doc = await api("PATCH", path, { ...again, version: fresh.version });
        }
        applyDoc(doc);
        return doc;
      } catch (err) {
        try {
          applyDoc(await api("GET", path));
        } catch {
          /* fail() below reports the original error */
        }
        fail(err);
        return null;
      } finally {
        mutatingRef.current -= 1;
        if (aliveRef.current) setBusy(false);
      }
    },
    [api, applyDoc, fail]
  );

  /** A pure EDL op from the edit step: optimistic, saved, replayed once on conflict. */
  const applyOp = useCallback(
    (op) => {
      const cur = projectRef.current;
      if (!cur) return null;
      const next = op(cur.edl);
      if (next === cur.edl) return null;
      setHistory((h) => pushHistory(h, cur.edl));
      show({ ...cur, edl: next });
      return mutate({ edl: next }, (fresh) => {
        const again = op(fresh.edl);
        return again === fresh.edl ? null : { edl: again };
      });
    },
    [mutate, show]
  );

  // Undo/redo send a snapshot; a snapshot is never replayed over someone else's change.
  const travel = (move) => {
    const cur = projectRef.current;
    const step = cur && move(history, cur.edl);
    if (!step) return;
    setHistory(step.history);
    show({ ...cur, edl: step.edl });
    mutate({ edl: step.edl }, () => null);
  };
  // Ctrl/Cmd+Z in the dialog is the Director's undo, never the editor's redux-undo (which would drop the project
  // back to Brief). Capture phase on document: runs before EditorApp's keyListener. Text fields keep native undo.
  const travelRef = useRef(travel);
  travelRef.current = travel;
  useEffect(() => {
    const onKey = (e) => {
      const intent = getUndoRedoIntent(e);
      if (!intent || !(tabInDialog(e) || e.target === document.body)) return;
      e.stopPropagation();
      if (/^(INPUT|TEXTAREA|SELECT)$/.test(e.target.tagName) || isContentEditableFocus(e.target))
        return;
      e.preventDefault();
      travelRef.current(intent === "redo" ? redo : undo);
    };
    document.addEventListener("keydown", onKey, true);
    return () => document.removeEventListener("keydown", onKey, true);
  }, []);

  // Insert a generated shot at the playhead: split there when it is inside a segment (PATCH),
  // then continue from the left half (the server inserts the new segment right after it).
  const continueFrom = useCallback(
    async (playhead, next, endFrame, model) => {
      const cur = projectRef.current;
      const frame = Math.round(playhead);
      if (!cur || !(frame > 0)) {
        setError("generic");
        return;
      }
      let edl = cur.edl;
      const rightId = newSegId();
      if (splitAt(edl, frame, rightId) !== edl) {
        const saved = await applyOp((e) => splitAt(e, frame, rightId));
        if (!saved) return;
        edl = saved.edl;
      }
      const hit = locate(edl, frame - 1);
      if (
        !hit ||
        segmentStart(edl, hit.index) + hit.segment.outFrame - hit.segment.inFrame !== frame
      ) {
        setError("generic");
        return;
      }
      const { takeId, outFrame, id } = hit.segment;
      const body = { takeId, frame: outFrame, afterSegmentId: id };
      const text = String(next || "")
        .trim()
        .slice(0, 500);
      if (text) body.next = text;
      if (endFrame) body.endFrame = endFrame;
      if (model) body.model = model;
      await runJob("continue", body);
    },
    [applyOp, runJob]
  );

  // Every "new video" path replaces the project on screen (the old one stays in the drafts list).
  const startProject = async (path, extra) => {
    setBusy(true);
    setError(null);
    setReused(false);
    try {
      const res = await api("POST", path, { trainingId: tid, navId, ...extra });
      if (aliveRef.current) setReused(res.reused ? (res.project || res).status : false);
      sentEdlRef.current = null;
      setAutoShot(null);
      setMissing(false);
      setBriefDraft(null);
      setBriefTried(false);
      setTreatDraft(null);
      setHistory(emptyHistory);
      setView(null);
      setDrawer(null);
      setStoryOpen(false);
      show(res.project || res);
    } catch (err) {
      fail(err);
    } finally {
      if (aliveRef.current) setBusy(false);
    }
  };
  const sceneText = hasText ? { narrationText: sceneNarrationText.slice(0, 20000) } : {};
  const create = () => startProject("projects", sceneText);
  const createOwnIdea = () => startProject("projects", { startMode: "prompt" });
  const createLd = () => startProject("projects", { flow: "ld", ...sceneText });

  // Empty Edit step → "Import video from library": the API has no import-into-project, so a new import
  // project replaces this one on screen (the empty one stays in the drafts list).
  const importVideo = async (url) => {
    setBusy(true);
    setError(null);
    try {
      const res = await api("POST", "projects/import", { trainingId: tid, navId, url });
      sentEdlRef.current = null;
      setHistory(emptyHistory);
      setView(null);
      setAutoShot(null);
      show(res.project);
    } catch (err) {
      fail(err);
    } finally {
      if (aliveRef.current) setBusy(false);
    }
  };

  const toggleCredit = async () => {
    if (creditOpen) {
      setCreditOpen(false);
      return;
    }
    setCreditLoading(true);
    try {
      setCredit(withCaps(await api("GET", "credit")));
      setCreditOpen(true);
    } catch (err) {
      fail(err);
    } finally {
      if (aliveRef.current) setCreditLoading(false);
    }
  };

  const openDrafts = async () => {
    setError(null);
    setDrafts({ items: undefined });
    try {
      const items = await api("GET", `projects/list?trainingId=${encodeURIComponent(tid || "")}`);
      if (aliveRef.current) setDrafts({ items: items || [] });
    } catch (err) {
      if (aliveRef.current) setDrafts({ items: [] });
      fail(err);
    }
  };

  const openDraft = async (id) => {
    setBusy(true);
    setError(null);
    try {
      const doc = await api("GET", `projects/${id}`);
      sentEdlRef.current = null;
      setMissing(false);
      setBriefDraft(null);
      setTreatDraft(null);
      setHistory(emptyHistory);
      setView(null);
      setDrawer(null);
      setDrafts(null);
      show(doc);
    } catch (err) {
      if (err?.status === 404) setError("stale");
      else fail(err);
    } finally {
      if (aliveRef.current) setBusy(false);
    }
  };

  const newVideoButton = (
    <button
      type="button"
      className="add-video-modal__btn add-video-modal__btn--ghost"
      style={{
        padding: "6px 12px",
        fontSize: "13px",
        display: "flex",
        alignItems: "center",
        gap: "6px",
        whiteSpace: "nowrap",
      }}
      disabled={busy}
      aria-haspopup="menu"
      onClick={(e) => {
        const r = e.currentTarget.getBoundingClientRect();
        setNewMenu({ x: r.left, y: r.bottom + 4, yAlt: r.top - 4 });
      }}
    >
      <LuFilePlus size={14} />
      {t("NewVideo")}
      <LuChevronDown size={12} />
    </button>
  );
  const newVideoMenu = newMenu && (
    <ActionMenu
      x={newMenu.x}
      y={newMenu.y}
      yAlt={newMenu.yAlt}
      actions={[
        {
          key: "scene",
          Icon: LuClapperboard,
          label: `${t("StartCinematic")} · ${t("AnalysisCost")}`,
          onClick: create,
          disabled: !hasText,
          title: hasText ? undefined : t("NeedsNarration"),
        },
        {
          key: "ld",
          Icon: LuGraduationCap,
          label: `${tl("StartLd")} · ${t("AnalysisCost")}`,
          onClick: createLd,
          disabled: !hasText,
          title: hasText ? undefined : t("NeedsNarration"),
        },
        { key: "idea", Icon: LuLightbulb, label: t("StartOwnPrompt"), onClick: createOwnIdea },
      ]}
      onClose={() => setNewMenu(null)}
    />
  );

  const draftsButton = (
    <button
      type="button"
      onClick={openDrafts}
      title={t("Drafts")}
      aria-label={t("Drafts")}
      className="app-modal__toggle-btn"
    >
      <LuFolderOpen size={16} />
    </button>
  );

  if (drafts) {
    return (
      <div className="add-video-modal__storyboard add-video-modal__storyboard--director">
        <div
          className="add-video-modal__storyboard-header"
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: "16px",
          }}
        >
          <span className="add-video-modal__storyboard-title">{t("Drafts")}</span>
          <button
            type="button"
            className="app-modal__btn app-modal__btn--ghost"
            onClick={() => setDrafts(null)}
          >
            {tv("Back")}
          </button>
        </div>
        {error && (
          <div role="alert" style={{ ...hintStyle, marginBottom: 12 }}>
            {t(`Errors.${error}`)}
          </div>
        )}
        {drafts.items === undefined && <Processing label={t("Loading")} />}
        {drafts.items?.length === 0 && (
          <div style={{ fontSize: 13, color: "#64748b" }}>{t("DraftsEmpty")}</div>
        )}
        <div className="add-video-modal__vd-grid add-video-modal__vd-grid--board">
          {(drafts.items || []).map((d) => (
            <div
              key={d.id}
              role="button"
              tabIndex={0}
              aria-label={d.title || t("Untitled")}
              className="add-video-modal__scene-card add-video-modal__vd-card"
              style={{ cursor: busy ? "wait" : "pointer" }}
              onClick={() => !busy && openDraft(d.id)}
              onKeyDown={(e) => {
                if ((e.key === "Enter" || e.key === " ") && !busy) {
                  e.preventDefault();
                  openDraft(d.id);
                }
              }}
            >
              <div className="add-video-modal__vd-img">
                {d.thumbUrl &&
                  (d.thumbKind === "video" ? (
                    <video
                      src={d.thumbUrl}
                      preload="metadata"
                      muted
                      playsInline
                      style={{ objectFit: "contain" }}
                    />
                  ) : (
                    <img src={d.thumbUrl} alt="" style={{ objectFit: "contain" }} />
                  ))}
              </div>
              <div className="add-video-modal__vd-name">{d.title || t("Untitled")}</div>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
                <span className="add-video-modal__vd-chip">
                  {d.origin === "import"
                    ? t("Imported")
                    : d.origin === "blank"
                      ? t("OriginBlank")
                      : t("OriginDirector")}
                </span>
                <span className="add-video-modal__vd-chip">
                  {t(`DraftStatus.${d.status}`, { defaultValue: d.status })}
                </span>
                {d.renderReady && <span className="add-video-modal__vd-chip">{t("Rendered")}</span>}
              </div>
              <div style={{ fontSize: 12, color: "#64748b" }}>
                {t("DraftSeconds", { n: Math.round(d.totalSec || 0) })} ·{" "}
                {t("DraftShots", { count: d.shots || 0 })} · {relativeTime(d.updatedAt)}
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (project === undefined) return <Processing label={t("Loading")} />;

  if (project === null) {
    return (
      <div className="add-video-modal__storyboard add-video-modal__storyboard--director">
        <div className="add-video-modal__vd-empty" role="region" aria-label={t("Tab")}>
          {missing && (
            <div role="alert" style={hintStyle}>
              {t("Errors.stale")}
            </div>
          )}
          {error && (
            <div role="alert" style={hintStyle}>
              {t(`Errors.${error}`)}
            </div>
          )}
          <LuClapperboard size={36} color="#94a3b8" aria-hidden="true" />
          <div className="add-video-modal__status-title">{t("Start")}</div>
          <StartOptions
            busy={busy}
            hasNarration={hasText}
            onCinematic={create}
            onLd={createLd}
            onPrompt={createOwnIdea}
          />
          <div>{draftsButton}</div>
        </div>
      </div>
    );
  }

  const status = project.status;
  const noStoryboard = project.origin === "import" || project.origin === "blank"; // no treatment / keyframes
  const step = (status === "editing" && view) || STEP_BY_STATUS[status] || "brief";
  const aspect = project.brief?.aspect;
  // Imported animated image ("Edit as video"): a trimmed Edit step, full UI behind the "Advanced editor" link.
  const simple = project.promptProfile === "animate" && !showAdvanced;
  // Brief / Treatment / Storyboard only matter to the AI steps; editing shows just Edit · Finish.
  const editOnly = status === "editing" && (step === "edit" || step === "finish");
  const stepList = simple || editOnly ? ["edit", "finish"] : STEPS;

  const nextBrief = async () => {
    if (briefIssues(briefDraft, project.refs).length) {
      setBriefTried(true);
      setTimeout(focusFirstMissing); // after the markers have rendered
      return;
    }
    const { required, ...brief } = briefDraft; // eslint-disable-line no-unused-vars
    if (await mutate({ brief }, () => ({ brief }))) await runJob("treatment");
  };
  const confirm = async () => {
    const fields = treatmentPatch(treatDraft);
    if (await mutate(fields, () => fields)) await runJob("confirm");
  };
  const approve = (pick) =>
    mutate(approvalPatch(project.shots, pick), (fresh) => approvalPatch(fresh.shots, pick));
  const setApproved = (shotId, value) =>
    approve((s) => (s.id === shotId ? value : s.keyframe?.approved));
  const approveAll = () => approve((s) => s.keyframe?.approved || s.keyframe?.status === "ready");
  // storyboard: every approved shot; editing: only approved shots that have no take yet.
  const takeShotIds = project.shots
    .filter(
      (s) =>
        s.keyframe?.approved &&
        s.keyframe?.status === "ready" &&
        (status === "storyboard" || s.takes.length === 0)
    )
    .map((s) => s.id);
  // Only a ready + approved keyframe may start a take (server: 400 keyframe.not_approved).
  // A continue shot has no keyframe: the server re-runs the continue (R30).
  const takeShotOk = (shotId) => {
    const s = project.shots.find((x) => x.id === shotId);
    const k = s?.keyframe;
    return (
      !!s &&
      (isContinueShot(s) ||
        s.startMode === "none" ||
        (!!k && k.status === "ready" && (!!k.approved || s.startMode === "ref")))
    );
  };
  const vModel = effectiveModel(credit?.models, "video", videoModel);
  const ldDraft = project.flow === "ld" && ldDraftOn(project.brief); // first takes are cheap drafts
  const takeModel = ldDraft ? DRAFT_MODEL : vModel;
  const draftIds = project.flow === "ld" && status === "editing" ? draftShotIds(project) : [];
  const iModel = effectiveModel(credit?.models, "image", imageModel);
  const unit = (kind, model, fallback) =>
    credit?.models?.[kind]?.[model] ?? (Number(fallback) || 0);
  const addRef = async (kind, body) => {
    const alone = addsInFlight.current++ === 0;
    const known = new Set((projectRef.current?.refs || []).map((r) => r.id));
    try {
      const res = await runJob(`refs/${kind}`, body);
      if (!res) return null;
      if (res.refId) return res.refId;
      // Fallback for a server without refId: the before/after diff is only safe when nothing else was adding.
      if (!alone || addsInFlight.current > 1) return null;
      return (projectRef.current?.refs || []).find((r) => !known.has(r.id))?.id ?? null;
    } finally {
      addsInFlight.current--;
    }
  };
  // The existing upload flow, then the url joins the reference set; resolves to the new ref's id.
  const uploadRef = (file, extra) =>
    new Promise((resolve) => {
      if (!uploadFunction) return resolve(null);
      uploadFunction(
        file,
        "",
        (url) => {
          if (url) return addRef("upload", { url, ...extra }).then(resolve);
          setError("generic");
          return resolve(null);
        },
        () => resolve(null)
      );
    });
  // refs/upload answers before the image is ready; from-ref needs a ready one (poll until it is).
  const waitRefReady = async (refId) => {
    for (let i = 0; i < 40; i += 1) {
      const ref = (projectRef.current?.refs || []).find((x) => x.id === refId);
      if (ref?.status === "ready" && ref.url) return true;
      if (!ref || ref.status === "failed") return false;
      await new Promise((resolve) => setTimeout(resolve, 1500));
      if (!aliveRef.current) return false;
      await refresh();
    }
    return false;
  };
  // Storyboard card → "Use image from library": into the reference set, then it becomes the shot's keyframe.
  const libraryKeyframe = async (shotId, url) => {
    setDrawer((d) => d && { ...d, v: { ...d.v, lib: false, preparing: true } });
    const refId = await addRef("upload", { url });
    if (refId && (await waitRefReady(refId)))
      await runJob(`shots/${shotId}/keyframe/from-ref`, { refId });
    else if (aliveRef.current) setError("refs_invalid");
    if (aliveRef.current) setDrawer(null);
  };
  const generateTakes = async () => {
    if (
      takeShotIds.length > 0 &&
      !(await runJob("takes", {
        shotIds: takeShotIds,
        model: takeModel,
        ...(ldDraft ? { draft: true } : {}),
      }))
    )
      return;
    setView(null);
  };
  // L&D: drafts are finalized on the model picked in the finalize strip; the server swaps each draft in the cut.
  const runFinalize = async (shotIds) => {
    // A priced pick is sent as is: effectiveModel would fold "veo" into the draft's "veo-fast" (one shown Omni).
    const model =
      typeof credit?.models?.video?.[finalModel] === "number"
        ? finalModel
        : effectiveModel(credit?.models, "video", finalModel);
    if (await runJob("takes", { shotIds, model, finalize: true })) setFinalizeOpen(false);
  };

  // "From this course" actions; excluding only matters before the treatment runs.
  const cookbookView = {
    data: { cast: [], locations: [], ...cookbook },
    use: project.cookbookUse ?? "all",
    saveNew: project.cookbookSaveNew !== false,
    exclude: project.cookbookExclude || [],
    canExclude: status === "brief",
    onUse: (cookbookUse) => mutate({ cookbookUse }, () => ({ cookbookUse })),
    onSaveNew: (cookbookSaveNew) => mutate({ cookbookSaveNew }, () => ({ cookbookSaveNew })),
    onToggle: (id) => {
      const known = [...cookbookView.data.cast, ...cookbookView.data.locations].map((e) => e.id);
      const cur = (projectRef.current?.cookbookExclude || []).filter((x) => known.includes(x));
      const next = cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id];
      mutate({ cookbookExclude: next }, () => ({ cookbookExclude: next }));
    },
    onRename: (id, name) =>
      api("PATCH", `cookbook/${id}?trainingId=${encodeURIComponent(tid)}`, { name })
        .then((c) => aliveRef.current && setCookbook(c))
        .catch(fail),
    onRemove: (id) =>
      api("DELETE", `cookbook/${id}?trainingId=${encodeURIComponent(tid)}`)
        .then((c) => aliveRef.current && setCookbook(c))
        .catch(fail),
  };

  const renderBrief = () => {
    // Cinematic | L&D, in place: only while the brief is open; the PATCH re-prefills the other path.
    const flowSwitch = !noStoryboard && (status === "brief" || status === "prefilling") && (
      <div
        className="add-video-modal__ai-ratio"
        role="group"
        aria-label={t("FlowSwitch")}
        aria-busy={busy || status === "prefilling"}
        style={{ marginBottom: 12 }}
      >
        {[
          ["freestyle", t("FlowCinematic")],
          ["ld", tl("FlowLd")],
        ].map(([id, label]) => {
          const active = (isLdForm(project, briefDraft ?? project.brief) ? "ld" : "freestyle") === id;
          return (
            <button
              key={id}
              type="button"
              aria-pressed={active}
              title={id === "ld" && !active && !project.brief?.ld && project.brief?.narration?.trim() ? t("AnalysisCostTip") : undefined}
              disabled={busy || status !== "brief"}
              onClick={async () => {
                if (active) return;
                setBriefDraft(null);
                setSwitchingTo(id);
                const doc = await mutate({ flow: id }, () => ({ flow: id }));
                if (aliveRef.current) {
                  setSwitchingTo(null);
                  setBriefDraft(null); // the draft re-seeded from the old doc while the PATCH was in flight
                  if (!doc || (doc.status === "brief" && isLdForm(doc, doc.brief) !== (id === "ld"))) setError("flow_switch");
                }
              }}
              className={`add-video-modal__ai-chip${active ? " add-video-modal__ai-chip--active" : ""}`}
            >
              {label}
              {switchingTo === id && <span className="add-video-modal__scene-spinner" />}
            </button>
          );
        })}
        {status === "prefilling" && <span className="add-video-modal__scene-spinner" />}
      </div>
    );
    if (status === "prefilling" && !project.error)
      return (
        <>
          {flowSwitch}
          <Processing label={t("Prefilling")} />
          {["objective", "narration"].map((key) => (
            <div key={key} aria-hidden="true" style={{ marginTop: 12 }}>
              <div style={skeletonBar} />
              <div style={{ ...skeletonBar, width: "70%" }} />
            </div>
          ))}
        </>
      );
    const d = briefDraft;
    if (!d) return null;
    const issues = briefIssues(d, project.refs).filter(
      (k) => briefTried || (k === "narration" && wordCount(d.narration) > MAX_NARRATION_WORDS)
    );
    const set = (patch) => setBriefDraft({ ...d, ...patch });
    const ownIdea = d.startMode === "prompt";
    const ld = isLdForm(project, d); // the separate L&D path; freestyle renders as before
    const castMode = d.castMode || (ld && d.ld.useCase === "concept" ? "none" : "ai");
    const photos = d.castPhotos || [];
    const setPhoto = (i, patch) =>
      set({ castPhotos: photos.map((c, j) => (j === i ? { ...c, ...patch } : c)) });
    const choice = (label, current, options, onPick) => (
      <div className="add-video-modal__ai-ratio" role="group" aria-label={label}>
        {options.map(([id, text]) => (
          <button
            key={id}
            type="button"
            aria-pressed={current === id}
            onClick={() => onPick(id)}
            className={`add-video-modal__ai-chip${current === id ? " add-video-modal__ai-chip--active" : ""}`}
          >
            {text}
          </button>
        ))}
      </div>
    );
    const text = (key, multiline, extra) => (
      <Field
        key={key}
        label={key === "narration" && (ownIdea || ld) ? t("Fields.voiceOver") : t(`Fields.${key}`)}
        value={d[key]}
        multiline={multiline}
        missing={issues.includes(key)}
        required={!(ownIdea && key === "narration") && (d.required || []).includes(key)}
        onChange={(v) => set({ [key]: v })}
      >
        {extra}
      </Field>
    );
    return (
      <>
        {flowSwitch}
        {ld && <LdTop d={d} set={set} issues={issues} Field={Field} />}
        <div style={{ display: "flex", gap: 24, flexWrap: "wrap", marginBottom: 12 }}>
          {!ld && (
          <div className="add-video-modal__ai-section">
            <div className="add-video-modal__ai-label">{t("StartFrom")}</div>
            {choice(
              t("StartFrom"),
              d.startMode,
              [
                ["scene", t("StartScene")],
                ["prompt", t("StartPrompt")],
              ],
              (startMode) => set({ startMode })
            )}
          </div>
          )}
          <div className="add-video-modal__ai-section">
            <div className="add-video-modal__ai-label">
              {t("Characters")}
              {castMode === "photos" && <Mark missing={issues.includes("castPhotos")} />}
            </div>
            {choice(
              t("Characters"),
              castMode,
              [
                ["ai", t("CastAi")],
                ["photos", t("CastPhotos")],
                ["none", t("CastNone")],
              ],
              (mode) => set({ castMode: mode })
            )}
          </div>
        </div>
        {ownIdea && (
          <Field
            label={t("Fields.scenario")}
            value={d.scenario}
            multiline
            rows={6}
            maxLength={MAX_SCENARIO}
            placeholder={t("ScenarioPlaceholder")}
            missing={issues.includes("scenario")}
            required
            onChange={(v) => set({ scenario: v })}
          >
            <div style={{ fontSize: 11, color: "#94a3b8", marginTop: 4 }}>
              {t("WordsCount", { words: wordCount(d.scenario) })}
            </div>
          </Field>
        )}
        {!ld && text("learningGoal", true)}
        {text(
          "narration",
          true,
          <div style={{ fontSize: 11, color: "#94a3b8", marginTop: 4 }}>
            {t("Words", { words: wordCount(d.narration) })}
            {wordCount(d.narration) > MAX_NARRATION_WORDS && (
              <span style={{ marginLeft: 8, color: "#b45309" }}>{t("NarrationTooLong")}</span>
            )}
          </div>
        )}
        {castMode === "photos" && (
          <div style={{ marginBottom: 12 }}>
            <label style={{ display: "flex", gap: 8, alignItems: "center", fontSize: 13 }}>
              <input
                type="checkbox"
                checked={likenessOk}
                onChange={(e) => setLikenessOk(e.target.checked)}
              />
              {t("LikenessConsent")}
            </label>
            {photos.map((c, i) => {
              const ref = (project.refs || []).find((r) => r.id === c.refId);
              const url = ref?.status === "ready" ? ref.url : null;
              return (
                <div
                  key={c.refId}
                  style={{ display: "flex", gap: 8, alignItems: "center", marginTop: 8 }}
                >
                  <PreviewThumb
                    preview={
                      url ? { items: [{ url, name: c.name, description: c.role }], index: 0 } : null
                    }
                    clickable
                  >
                    <div style={{ width: 56, height: 56 }}>
                      {url ? (
                        <img
                          src={url}
                          alt=""
                          style={{ width: 56, height: 56, objectFit: "cover", borderRadius: 6 }}
                        />
                      ) : (
                        ref?.status !== "failed" && (
                          <span className="add-video-modal__scene-spinner" />
                        )
                      )}
                    </div>
                  </PreviewThumb>
                  <input
                    className="add-video-modal__scene-prompt-textarea"
                    style={{ ...inputStyle, width: 160 }}
                    maxLength={60}
                    aria-label={t("PhotoName")}
                    placeholder={t("PhotoName")}
                    value={c.name}
                    onChange={(e) => setPhoto(i, { name: e.target.value })}
                  />
                  <input
                    className="add-video-modal__scene-prompt-textarea"
                    style={inputStyle}
                    maxLength={200}
                    aria-label={t("PhotoRole")}
                    placeholder={t("PhotoRole")}
                    value={c.role}
                    onChange={(e) => setPhoto(i, { role: e.target.value })}
                  />
                  <button
                    type="button"
                    className="add-video-modal__btn add-video-modal__btn--secondary"
                    style={smallBtn}
                    aria-label={t("PhotoRemove")}
                    title={t("PhotoRemove")}
                    onClick={() => set({ castPhotos: photos.filter((_, j) => j !== i) })}
                  >
                    <LuTrash2 size={12} />
                  </button>
                </div>
              );
            })}
            <button
              type="button"
              className="add-video-modal__btn add-video-modal__btn--secondary"
              style={{ ...smallBtn, marginTop: 8 }}
              disabled={!likenessOk || busy || photos.length >= MAX_CAST_PHOTOS}
              onClick={pickPhoto}
            >
              {t("AddPhoto")}
            </button>
            {photoInput(t("AddPhoto"))}
          </div>
        )}
        <div className="add-video-modal__vd-brief-grid">
          {!ld && text("audience", false)}
          <div style={{ marginBottom: 12 }}>
            <div className="add-video-modal__scene-field-label" style={labelStyle}>
              {t("Fields.language")}
            </div>
            <select
              className="add-video-modal__scene-prompt-textarea"
              style={inputStyle}
              aria-label={t("Fields.language")}
              value={d.language}
              onChange={(e) => set({ language: e.target.value })}
            >
              {!languageOptions.some((o) => o.value.split("-")[0] === d.language) && (
                <option value={d.language}>{d.language}</option>
              )}
              {languageOptions.map((o) => (
                <option key={o.value} value={o.value.split("-")[0]}>
                  {o.label}
                </option>
              ))}
            </select>
          </div>
          {!ld && (
          <div>
            <Field
              label={t("Fields.targetSec")}
              type="number"
              min={10}
              max={120}
              value={d.targetSec}
              missing={issues.includes("targetSec")}
              required
              note={issues.includes("targetSec") && d.targetSec !== "" ? t("TargetRange") : undefined}
              onChange={(v) => set({ targetSec: v === "" ? "" : Math.round(Number(v)) })}
            />
          </div>
          )}

          <div className="add-video-modal__ai-section">
            <div className="add-video-modal__ai-label">{tv("AspectRatio")}</div>
            <div className="add-video-modal__ai-ratio">
              {ASPECTS.map((ratio) => {
                const maxSide = Math.max(ratio.w, ratio.h);
                const width = Math.max(12, Math.round((ratio.w / maxSide) * 22));
                const height = Math.max(12, Math.round((ratio.h / maxSide) * 22));
                return (
                  <button
                    key={ratio.id}
                    type="button"
                    onClick={() => set({ aspect: ratio.id })}
                    className={`add-video-modal__ai-chip${d.aspect === ratio.id ? " add-video-modal__ai-chip--active" : ""}`}
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "space-between",
                      gap: "6px",
                      minHeight: "52px",
                    }}
                  >
                    <span
                      className="add-video-modal__ai-chip-box"
                      style={{ width, height, marginTop: "auto" }}
                    />
                    <span style={{ marginTop: "auto" }}>{ratio.id}</span>
                  </button>
                );
              })}
            </div>
          </div>

          {(!ld || lookOpen) && (
          <div className="add-video-modal__ai-section add-video-modal__vd-full">
            <div className="add-video-modal__ai-label">
              {tv("Style")}
              {(d.required || []).includes("styleKey") && <Mark missing={issues.includes("styleKey")} />}
            </div>
            <div className="add-video-modal__ai-style-grid">
              {videoStyles.map((s) => {
                const active = d.styleKey === s.styleKey;
                const image =
                  s.sampleVideoUrl ||
                  s.thumbnail ||
                  s.sampleImageUrl ||
                  styleThumb(s.styleKey) ||
                  null;
                const label = s.name || s.promptText || s.styleKey || s.id;
                return (
                  <button
                    key={s.styleKey}
                    type="button"
                    onClick={() => set({ styleKey: s.styleKey })}
                    className={`add-video-modal__ai-style-card${active ? " add-video-modal__ai-style-card--active" : ""}`}
                    style={{ position: "relative", overflow: "hidden", minHeight: "100px" }}
                  >
                    {image ? (
                      React.createElement(isVideoUrl(image) ? "video" : "img", {
                        src: image,
                        ...(isVideoUrl(image)
                          ? { muted: true, loop: true, autoPlay: true, playsInline: true }
                          : { alt: "" }),
                        style: {
                          position: "absolute",
                          top: "6px",
                          left: "6px",
                          right: "6px",
                          width: "calc(100% - 12px)",
                          height: "72px",
                          objectFit: "cover",
                          borderRadius: "10px",
                        },
                      })
                    ) : (
                      <span className="add-video-modal__ai-style-card-placeholder">{label}</span>
                    )}
                    <span
                      style={{ position: "relative", zIndex: 1, marginTop: image ? "78px" : "0" }}
                    >
                      {label}
                    </span>
                    {active && (
                      <div className="add-video-modal__ai-style-check">
                        <LuCheck size={12} />
                      </div>
                    )}
                  </button>
                );
              })}
              <button
                type="button"
                onClick={() => set({ styleKey: null })}
                className={`add-video-modal__ai-style-card add-video-modal__ai-style-card--custom${
                  d.styleKey === null ? " add-video-modal__ai-style-card--active" : ""
                }`}
              >
                <div className="add-video-modal__ai-style-custom-icon">
                  <LuPenLine size={24} />
                </div>
                <span>{tv("CustomCardLabel")}</span>
                {d.styleKey === null && (
                  <div className="add-video-modal__ai-style-check">
                    <LuCheck size={12} />
                  </div>
                )}
              </button>
            </div>
            {d.styleKey === null && (
              <div className="add-video-modal__ai-custom-desc">
                <label>{t("Fields.customStyle")}</label>
                <textarea
                  value={d.customStyle}
                  maxLength={2000}
                  onChange={(e) => set({ customStyle: e.target.value })}
                  placeholder={tv("CustomStylePlaceholder")}
                  rows={3}
                />
              </div>
            )}
          </div>
          )}
        </div>

        {ld && (
          <LdBottom
            d={d}
            set={set}
            credit={credit}
            videoModel={videoModel}
            videoStyles={videoStyles}
            apiUrl={apiUrl}
            projectId={project?.id}
            lookOpen={lookOpen}
            onLook={() => setLookOpen((o) => !o)}
          />
        )}
        {!ld &&
          BRIEF_NOTES.filter((key) => !(key === "cast" && castMode === "none")).map((key) =>
            text(key, true)
          )}
        <CookbookControl cookbook={cookbookView} />
        {castMode !== "none" && <CookbookSection kind="cast" cookbook={cookbookView} />}
        <CookbookSection kind="locations" cookbook={cookbookView} />
        {!ld && (
          <BriefModelRow
            models={credit?.models}
            value={d.videoModel ?? effectiveModel(credit?.models, "video", videoModel)}
            onChange={(m) => set({ videoModel: m })}
          />
        )}
      </>
    );
  };

  const renderTreatment = () => {
    if (status === "treating" && !project.error) {
      const landed = ["cast", "locations"].flatMap((kind) =>
        (project.bible?.[kind] || []).map((m) => ({
          kind,
          m,
          still: m.still,
          name: m.name,
          description: m.description,
        }))
      );
      const previewOf = gallery(landed);
      return (
        <div className="add-video-modal__vd-stage">
          <Processing label={t("Treating")} />
          {["logline", "look"].map((key) => (
            <div key={key} className="add-video-modal__vd-block" aria-hidden="true">
              <div className="add-video-modal__vd-label">{t(`Fields.${key}`)}</div>
              <div style={skeletonBar} />
              <div style={{ ...skeletonBar, width: "70%" }} />
            </div>
          ))}
          {landed.length > 0 && (
            <div className="add-video-modal__vd-cards">
              {landed.map((entry) => {
                const { kind, m } = entry;
                return (
                  <div
                    key={`${kind}${m.id}`}
                    className="add-video-modal__scene-card add-video-modal__vd-card"
                  >
                    <Frame still={m.still} preview={previewOf(entry)} />
                    <div className="add-video-modal__vd-name">{m.name}</div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      );
    }
    const d = treatDraft;
    if (!d) return null;
    const set = (patch) => setTreatDraft({ ...d, ...patch });
    const stillOf = (kind, id) => project.bible?.[kind].find((m) => m.id === id)?.still;
    const block = (key) => (
      <div key={key} className="add-video-modal__vd-block">
        <Pencil
          label={t(`Fields.${key}`)}
          onClick={() => setDrawer({ kind: key, i: 0, v: { [key]: d[key] } })}
        />
        <div className="add-video-modal__vd-label">
          {t(`Fields.${key}`)}
          {key === "logline" && <Mark missing={!d.logline.trim()} />}
        </div>
        <div className="add-video-modal__vd-clamp">{d[key]}</div>
      </div>
    );
    return (
      <div className="add-video-modal__vd-stage">
        {project.warnings?.includes("cookbook.style_mismatch") && (
          <div role="status" style={{ fontSize: 12, color: "#64748b", marginBottom: 8 }}>
            {t("Cookbook.StyleMismatch")}
          </div>
        )}
        <div className="add-video-modal__vd-grid">
          <div className="add-video-modal__vd-zone">
            {block("logline")}
            {block("look")}
          </div>
          <div className="add-video-modal__vd-zone">
            {["cast", "locations"].map((kind) => {
              const entries = d[kind].map((m) => ({
                still: stillOf(kind, m.id),
                name: m.name,
                description: m.description,
              }));
              const previewOf = gallery(entries);
              return (
                d[kind].length > 0 && (
                  <div key={kind}>
                    <div className="add-video-modal__ai-label">{t(`Fields.${kind}`)}</div>
                    <div className="add-video-modal__vd-cards">
                      {d[kind].map((m, i) => {
                        const still = stillOf(kind, m.id);
                        return (
                          <div
                            key={m.id}
                            className="add-video-modal__scene-card add-video-modal__vd-card"
                          >
                            <Pencil
                              label={m.name}
                              onClick={() =>
                                setDrawer({
                                  kind,
                                  i,
                                  v: { name: m.name, description: m.description },
                                })
                              }
                            />
                            <Frame still={still} preview={previewOf(entries[i])} />
                            {saferOf(still) && (
                              <SaferButton
                                onClick={() =>
                                  setDrawer({
                                    kind,
                                    i,
                                    v: { name: m.name, description: still.suggestedPrompt },
                                  })
                                }
                              />
                            )}
                            <div className="add-video-modal__vd-name">{m.name}</div>
                            {kind === "cast" &&
                              project.bible?.cast.find((c) => c.id === m.id)?.sourceRefId && (
                                <Badge status="ready" label={t("FromPhoto")} />
                              )}
                            <div className="add-video-modal__vd-clamp add-video-modal__vd-clamp--1">
                              {m.description}
                            </div>
                            {still && <Badge status={statusOf(still)} />}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )
              );
            })}
          </div>
          <div className="add-video-modal__vd-zone">
            {d.shots.map((s, i) => (
              <div key={s.id} className="add-video-modal__scene-card add-video-modal__vd-row">
                <Pencil
                  label={t("Shot", { n: i + 1 })}
                  onClick={() =>
                    setDrawer({
                      kind: "shot",
                      i,
                      v: {
                        narration: s.narration,
                        durationSec: s.durationSec,
                        camera: s.camera,
                        prompt: s.prompt,
                        keyframePrompt: s.keyframe?.prompt,
                      },
                    })
                  }
                />
                <div className="add-video-modal__scene-title">
                  <span className="add-video-modal__scene-num">{t("Shot", { n: i + 1 })}</span>
                  <span className="add-video-modal__scene-time">{s.durationSec}s</span>
                  <button
                    type="button"
                    className="add-video-modal__btn add-video-modal__btn--ghost"
                    aria-label={t("RemoveShot")}
                    title={t("RemoveShot")}
                    disabled={d.shots.length <= 1}
                    onClick={() => set({ shots: d.shots.filter((_, j) => j !== i) })}
                    style={{ padding: "2px 8px", fontSize: "12px", color: "#ef4444" }}
                  >
                    <LuTrash2 size={12} />
                  </button>
                </div>
                <div className="add-video-modal__vd-clamp add-video-modal__vd-clamp--2">
                  {s.narration}
                </div>
                {s.camera && <span className="add-video-modal__vd-chip">{s.camera}</span>}
              </div>
            ))}
          </div>
        </div>
        {renderDrawer()}
      </div>
    );
  };

  const renderStoryboard = () => {
    const entries = project.shots.map((s, i) => ({
      still: s.keyframe,
      name: t("Shot", { n: i + 1 }),
      description: s.keyframe?.prompt,
    }));
    const previewOf = gallery(entries);
    return (
      <div className="add-video-modal__vd-stage">
        <div className="add-video-modal__vd-grid add-video-modal__vd-grid--board">
          {project.shots.map((s, i) => {
            const kf = s.keyframe;
            const kfStatus = statusOf(kf);
            const color = (kf.approved ? BADGE.ready : BADGE[kfStatus] || BADGE.pending)[1];
            return (
              <div
                key={s.id}
                className="add-video-modal__scene-card add-video-modal__vd-card"
                style={{ borderColor: color }}
              >
                {kfStatus !== "pending" && (
                  <Pencil
                    label={t("Shot", { n: i + 1 })}
                    onClick={() => setDrawer({ kind: "kf", i: s.id, v: { prompt: kf.prompt } })}
                  />
                )}
                <Frame still={kf} preview={previewOf(entries[i])} />
                {saferOf(kf) && (
                  <SaferButton
                    onClick={() =>
                      setDrawer({ kind: "kf", i: s.id, v: { prompt: kf.suggestedPrompt } })
                    }
                  />
                )}
                <div className="add-video-modal__scene-title">
                  <span className="add-video-modal__scene-num">{t("Shot", { n: i + 1 })}</span>
                  <span className="add-video-modal__scene-time">{s.durationSec}s</span>
                  <Badge
                    status={kf.approved ? "ready" : kfStatus}
                    label={kf.approved ? t("Approved") : undefined}
                  />
                </div>
                <div className="add-video-modal__vd-clamp add-video-modal__vd-clamp--1">
                  {kf.prompt}
                </div>
                {s.narration?.trim() && (
                  <div
                    className="add-video-modal__vd-clamp add-video-modal__vd-clamp--2"
                    title={t("Fields.voiceOver")}
                    style={{ fontSize: 12, color: "#64748b", fontStyle: "italic" }}
                  >
                    {s.narration}
                  </div>
                )}
                <div className="add-video-modal__vd-actions">
                  {kfStatus === "ready" && (
                    <button
                      type="button"
                      className={`add-video-modal__btn ${kf.approved ? "add-video-modal__btn--ghost" : "add-video-modal__btn--primary"}`}
                      style={smallBtn}
                      disabled={busy}
                      onClick={() => setApproved(s.id, !kf.approved)}
                    >
                      {kf.approved ? t("Approved") : t("Approve")}
                    </button>
                  )}
                  {kfStatus !== "pending" && (
                    <button
                      type="button"
                      className="add-video-modal__btn add-video-modal__btn--secondary"
                      style={smallBtn}
                      disabled={busy}
                      onClick={() => runJob(`shots/${s.id}/keyframe`, { model: iModel })}
                    >
                      <LuRefreshCw size={12} />
                      {priced(t("Regenerate"), credit?.models, "image", iModel)}
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
        {renderDrawer()}
        {storyOpen && (
          <StoryAssetsPanel
            project={project}
            busy={busy}
            onClose={() => setStoryOpen(false)}
            onAddRef={status === "editing" ? (url) => addRef("upload", { url }) : undefined}
            cookbook={cookbookView}
            error={error ? t(`Errors.${error}`) : null}
            onDismissError={() => setError(null)}
          />
        )}
      </div>
    );
  };

  // R28: a treatment Save PATCHes at once (Confirm still PATCHes the final draft); replayed on 409.
  const saveDraft = (draft) => {
    setTreatDraft(draft);
    const fields = treatmentPatch(draft);
    mutate(fields, () => fields);
  };

  // One drawer for every edit. Treatment saves PATCH the draft; a keyframe prompt regenerates.
  const renderDrawer = () => {
    if (!drawer) return null;
    const { kind, i, v } = drawer;
    const patch = (p) => setDrawer({ ...drawer, v: { ...v, ...p } });
    const field = (key, label, extra) => (
      <Field label={label} value={v[key]} onChange={(x) => patch({ [key]: x })} {...extra} />
    );
    let title = t(`Fields.${kind}`);
    let invalid = false;
    let body;
    let save;
    if (kind === "logline") {
      invalid = !v.logline.trim();
      body = field("logline", title, { multiline: true, maxLength: 500, missing: invalid, required: true });
      save = () => saveDraft({ ...treatDraft, ...v });
    } else if (kind === "look") {
      body = field("look", title, { multiline: true });
      save = () => saveDraft({ ...treatDraft, ...v });
    } else if (kind === "cast" || kind === "locations") {
      title = treatDraft[kind][i].name || title;
      invalid = !v.name.trim();
      body = (
        <>
          {field("name", t("Fields.name"), { maxLength: 100, missing: invalid, required: true })}
          {field("description", t("Fields.description"), { multiline: true })}
        </>
      );
      save = () =>
        saveDraft({
          ...treatDraft,
          [kind]: treatDraft[kind].map((m, j) => (j === i ? { ...m, ...v } : m)),
        });
    } else if (kind === "shot") {
      title = t("Shot", { n: i + 1 });
      invalid = !(v.durationSec >= 1 && v.durationSec <= 8);
      body = (
        <>
          {field("narration", t("Fields.narration"), { multiline: true })}
          {field("durationSec", t("Fields.durationSec"), {
            type: "number",
            min: 1,
            max: 8,
            missing: invalid,
            required: true,
            onChange: (x) => patch({ durationSec: Number(x) }),
          })}
          {field("camera", t("Fields.camera"), { maxLength: 500 })}
          {field("prompt", t("Fields.prompt"), { multiline: true })}
          {field("keyframePrompt", t("Fields.keyframePrompt"), { multiline: true })}
        </>
      );
      save = () => {
        const { keyframePrompt, ...rest } = v;
        saveDraft({
          ...treatDraft,
          shots: treatDraft.shots.map((s, j) =>
            j === i ? { ...s, ...rest, keyframe: { ...s.keyframe, prompt: keyframePrompt } } : s
          ),
        });
      };
    } else {
      title = t("Shot", { n: project.shots.findIndex((s) => s.id === i) + 1 });
      invalid = busy || !v.prompt.trim();
      body = field("prompt", t("Fields.keyframePrompt"), { multiline: true, rows: 4 });
      if (v.preparing) {
        invalid = true;
        body = <Processing label={t("PreparingImage")} />;
      } else if (v.lib) {
        invalid = true;
        body = <LibraryPicker kind="image" onPick={({ url }) => libraryKeyframe(i, url)} />;
      } else if (status === "editing") {
        // Refs (and so a library image) exist only once the project is editing.
        body = (
          <>
            {body}
            <button
              type="button"
              className="add-video-modal__btn add-video-modal__btn--secondary"
              style={{ ...smallBtn, marginTop: 8 }}
              disabled={busy}
              onClick={() => patch({ lib: true })}
            >
              {t("UseLibraryImage")}
            </button>
          </>
        );
      }
      save = () => runJob(`shots/${i}/keyframe`, { prompt: v.prompt.trim(), model: iModel });
    }
    return (
      <Drawer
        title={title}
        saveLabel={
          kind === "kf" ? priced(t("Regenerate"), credit?.models, "image", iModel) : tv("Save")
        }
        saveDisabled={invalid}
        onCancel={() => setDrawer(null)}
        onSave={() => {
          save();
          setDrawer(null);
        }}
      >
        {body}
      </Drawer>
    );
  };

  const render = project.render;
  const renderStatus = render ? statusOf(render) : null;
  // A ready render of another cut is stale; so is one with no signature (made before signatures existed).
  const renderStale =
    renderStatus === "ready" &&
    render.edlSignature !== edlSignature(project.edl, project.shots, project.clipAudio);
  const renderFinish = () => (
    <>
      {renderStatus === "pending" ? (
        <Processing label={t("RenderingTitle")} note={t("RenderNote")} />
      ) : (
        <DirectorFinish
          project={project}
          audioUrl={audioUrl}
          showPreview={renderStatus !== "ready" || renderStale}
        />
      )}
      {renderStatus === "failed" && (
        <div className="add-video-modal__status-card add-video-modal__status-card--error">
          <div className="add-video-modal__status-title">✗ {errorText(render.error)}</div>
        </div>
      )}
      {renderStale && (
        <div role="alert" className="add-video-modal__director-finish-warn">
          {t("RenderStale")}
        </div>
      )}
      {renderStatus === "ready" && !renderStale && (
        <div className="add-video-modal__status-card add-video-modal__status-card--success">
          <div className="add-video-modal__status-title">✓ {tv("VideoReady")}</div>
          <video src={render.url} controls className="add-video-modal__status-video" />
        </div>
      )}
    </>
  );

  // Stepper: server status is the ceiling. Only an editing project can move between its steps.
  const reachable =
    status === "editing"
      ? {
          brief: !noStoryboard,
          treatment: !noStoryboard && !!project.bible,
          storyboard: !noStoryboard,
          edit: true,
          finish: project.edl.length > 0,
        }
      : {};
  const goStep = (id) => {
    setDrawer(null);
    setStoryOpen(false);
    setView(id === "edit" ? null : id);
  };

  // ── Footer per step: Back / Next + this step's credit estimate ──
  const stills = project.bible ? [...project.bible.cast, ...project.bible.locations] : [];
  const stillsPending = stills.some((m) => m.still && statusOf(m.still) === "pending");
  const draftShots = treatDraft?.shots || [];
  const totalSec = draftShots.reduce((sum, s) => sum + (Number(s.durationSec) || 0), 0);
  const anyKeyframeReady = project.shots.some((s) => s.keyframe?.status === "ready");
  const anyReady = project.shots.some((s) => s.takes.some((tk) => tk.status === "ready" && tk.url));
  let back = null;
  let next = null;
  let rows = [];
  let summary = null;
  let extras = [];
  if (step === "brief") {
    next = {
      label: t("Next"),
      disabled: status !== "brief" || !briefDraft,
      invalid: !!briefDraft && briefIssues(briefDraft, project.refs).length > 0,
      onClick: nextBrief,
    };
    if (credit)
      rows = [{ label: t("Credit.Stills"), cost: (Number(credit.imageCredits) || 0) * 8 }];
  } else if (step === "treatment") {
    next = {
      label: t("Confirm"),
      disabled:
        status !== "treatment" ||
        !treatDraft ||
        stillsPending ||
        !treatDraft.logline.trim() ||
        [...treatDraft.cast, ...treatDraft.locations].some((m) => !m.name.trim()) ||
        draftShots.length === 0 ||
        totalSec > 120 ||
        draftShots.some((s) => !(s.durationSec >= 1 && s.durationSec <= 8)),
      onClick: confirm,
    };
    if (credit) {
      rows = [
        {
          label: t("Credit.Keyframes"),
          cost: (Number(credit.imageCredits) || 0) * draftShots.length,
        },
        { label: t("Credit.Takes"), cost: (Number(credit.takeCredits) || 0) * draftShots.length },
      ];
    }
    if (treatDraft) summary = t("Summary", { count: draftShots.length, seconds: totalSec });
  } else if (step === "storyboard") {
    if (status === "editing") back = { label: tv("Back"), onClick: () => setView(null) };
    const takesCost = unit("video", takeModel, credit?.takeCredits) * takeShotIds.length;
    next = {
      label:
        credit && takesCost > 0
          ? `${t("Next")} · ${t("Models.PriceShort", { n: takesCost })}`
          : t("Next"),
      disabled:
        status === "storyboard" &&
        !project.shots.every((s) => s.keyframe?.approved && s.keyframe?.status === "ready"),
      title:
        status === "storyboard" &&
        !project.shots.every((s) => s.keyframe?.approved && s.keyframe?.status === "ready")
          ? t("NextNeedsApproval")
          : undefined,
      onClick: generateTakes,
    };
    if (credit)
      rows = [
        {
          label: t("Credit.Takes"),
          cost: unit("video", takeModel, credit.takeCredits) * takeShotIds.length,
        },
      ];
  } else if (step === "edit") {
    if (!noStoryboard) back = { label: tv("Back"), onClick: () => setView("storyboard") };
    // No Next until a clip is ready (the focused progress view has nothing to continue with).
    if (anyReady)
      next = {
        label: t("Next"),
        disabled: project.edl.length === 0,
        onClick: () => setView("finish"),
      };
    if (credit) rows = [{ label: t("Credit.Take"), cost: Number(credit.takeCredits) || 0 }];
  } else {
    back = { label: t("BackToEdit"), onClick: () => setView(null) };
    const rerender = {
      label: renderStatus === "ready" ? t("RenderAgain") : t("Render"),
      disabled: renderStatus === "pending" || project.edl.length === 0,
      onClick: () => runJob("render"),
    };
    if (renderStale) {
      next = rerender;
      extras = onOpenLibrary ? [{ label: t("OpenLibrary"), onClick: onOpenLibrary }] : [];
    } else if (renderStatus === "ready") {
      // Replace only when the Director was opened from a box (Replace File / Edit in Director); a library draft has none.
      next = {
        label: canReplace ? t("ReplaceVideo") : t("AddToScene"),
        onClick: () =>
          onAddToScene?.(render.url, t("VideoName"), undefined, { videoProjectId: project.id }),
      };
      extras = [
        ...(onOpenLibrary ? [{ label: t("OpenLibrary"), onClick: onOpenLibrary }] : []),
        rerender,
      ];
    } else if (renderStatus !== "pending") {
      next = rerender;
    }
  }

  // Revisiting Brief / Treatment from an editing project: shown, not editable (the server owns that rule).
  if (status === "editing" && (step === "brief" || step === "treatment")) {
    back = { label: t("BackToEdit"), onClick: () => setView(null) };
    next = null;
    rows = [];
    summary = null;
  }
  const ro = (node) =>
    status === "editing" ? (
      <fieldset disabled style={{ border: 0, margin: 0, padding: 0, minWidth: 0 }}>
        {node}
      </fieldset>
    ) : (
      node
    );

  return (
    <div className="vd-root add-video-modal__storyboard add-video-modal__storyboard--director">
      <PreviewHost>
        <div
          className="add-video-modal__storyboard-header"
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: "8px",
          }}
        >
          {simple && (
            <span style={{ fontSize: 15, fontWeight: 600, color: "#334155" }}>
              {t("EditVideoTitle")}
            </span>
          )}
          <ol
            className="add-video-modal__vd-stepper"
            aria-label={t("StepCount", { n: stepList.indexOf(step) + 1 })}
          >
            {stepList.map((id, i) => {
              const at = stepList.indexOf(step);
              const done = i < at;
              const body = (
                <>
                  <span className="add-video-modal__vd-step-dot" aria-hidden="true">
                    {done ? <LuCheck size={11} /> : i + 1}
                  </span>
                  {t(`Steps.${STEP_KEYS[id]}`)}
                </>
              );
              const cls = `add-video-modal__vd-step${id === step ? " add-video-modal__vd-step--current" : ""}${done ? " add-video-modal__vd-step--done" : ""}`;
              return (
                <li key={id} aria-current={id === step ? "step" : undefined}>
                  {reachable[id] && id !== step ? (
                    <button
                      type="button"
                      className={`${cls} add-video-modal__vd-step--link`}
                      disabled={busy}
                      onClick={() => goStep(id)}
                    >
                      {body}
                    </button>
                  ) : (
                    <span className={cls}>{body}</span>
                  )}
                </li>
              );
            })}
          </ol>
          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            {simple && (
              <button
                type="button"
                style={{
                  background: "none",
                  border: 0,
                  padding: 0,
                  fontSize: 12,
                  color: "#64748b",
                  textDecoration: "underline",
                  cursor: "pointer",
                }}
                onClick={() => setShowAdvanced(true)}
              >
                {t("AdvancedEditor")}
              </button>
            )}
            {step === "storyboard" && anyKeyframeReady && (
              <ModelPick
                kind="image"
                value={imageModel}
                onChange={setImageModel}
                models={credit?.models}
              />
            )}
            {step === "storyboard" && anyKeyframeReady && (
              <button
                type="button"
                className="add-video-modal__btn add-video-modal__btn--ghost"
                style={{
                  padding: "6px 12px",
                  fontSize: "13px",
                  display: "flex",
                  alignItems: "center",
                  gap: "6px",
                }}
                disabled={
                  busy ||
                  !project.shots.some(
                    (s) => s.keyframe?.status === "ready" && !s.keyframe?.approved
                  )
                }
                onClick={approveAll}
              >
                <LuCheck size={14} />
                {t("ApproveAll")}
              </button>
            )}
            {(step === "storyboard" || (step === "edit" && anyReady)) && !!project.bible && (
              <button
                type="button"
                className="add-video-modal__btn add-video-modal__btn--ghost"
                style={{
                  padding: "6px 12px",
                  fontSize: "13px",
                  display: "flex",
                  alignItems: "center",
                  gap: "6px",
                }}
                title={t("StoryAssetsHelper")}
                onClick={() => setStoryOpen(true)}
              >
                <LuBookOpen size={14} />
                {t("StoryAssets")}
              </button>
            )}
            {status === "editing" && !noStoryboard && (
              <button
                type="button"
                className="add-video-modal__btn add-video-modal__btn--ghost"
                style={{
                  padding: "6px 12px",
                  fontSize: "13px",
                  display: "flex",
                  alignItems: "center",
                  gap: "6px",
                  whiteSpace: "nowrap",
                }}
                disabled={busy}
                onClick={() => goStep("brief")}
              >
                <LuMap size={14} />
                {t("StartingPoint")}
              </button>
            )}
            {draftIds.length > 0 && (
              <button
                type="button"
                className="add-video-modal__btn add-video-modal__btn--ghost"
                style={{ padding: "6px 12px", fontSize: "13px", whiteSpace: "nowrap" }}
                disabled={busy}
                aria-expanded={finalizeOpen}
                onClick={() => setFinalizeOpen((o) => !o)}
              >
                {tl("Finalize")}
              </button>
            )}
            {newVideoButton}
            {newVideoMenu}
            {draftsButton}
          </div>
        </div>

        {project.error && <ErrorAlert text={errorText(project.error)} />}

        <div className={`add-video-modal__vd-content add-video-modal__vd-content--${step}`}>
          {step === "brief" && ro(renderBrief())}
          {step === "treatment" && ro(renderTreatment())}
          {step === "storyboard" && renderStoryboard()}
          {step === "edit" && (
            <div className="add-video-modal__vd-editwrap">
              {project.origin === "import" &&
                project.shots.some((s) => s.takes.some((tk) => tk.hasAudio && !tk.audioTrack)) && (
                  <div
                    role="status"
                    style={{
                      ...hintStyle,
                      marginTop: 0,
                      marginBottom: 12,
                      background: "#fffbeb",
                      border: "1px solid #fde68a",
                      color: "#92400e",
                    }}
                  >
                    {t("ImportAudioWarning")}
                  </div>
                )}
              {finalizeOpen && draftIds.length > 0 && (
                <FinalizeStrip
                  count={draftIds.length}
                  model={finalModel}
                  onModel={setFinalModel}
                  credit={credit}
                  busy={busy}
                  onRun={() => runFinalize(draftIds)}
                />
              )}
              <DirectorEditStep
                project={project}
                onFinalize={project.flow === "ld" ? (shotId) => runFinalize([shotId]) : undefined}
                onNewLd={createLd}
                onNewPrompt={createOwnIdea}
                hasNarration={hasText}
                initialVideoModel={videoModel}
                simple={simple}
                busy={busy}
                canUndo={history.past.length > 0}
                canRedo={history.future.length > 0}
                onOp={applyOp}
                onClipAudio={(clipAudio) => mutate({ clipAudio }, () => ({ clipAudio }))}
                onUndo={() => travel(undo)}
                onRedo={() => travel(redo)}
                onRegenerate={(shotId, model, endFrame, refIds) =>
                  takeShotOk(shotId) &&
                  runJob("takes", {
                    shotIds: [shotId],
                    model,
                    ...(endFrame ? { endFrame } : {}),
                    ...(refIds?.length ? { refIds } : {}),
                  })
                }
                onAddShot={(body) => runJob("shots", body)}
                onApproveShot={(shotId) => setApproved(shotId, true)}
                onRefUrl={(url) => addRef("upload", { url })}
                onImportVideo={importVideo}
                onNewFromScene={create}
                autoAddShot={autoShot}
                onAutoAddShotDone={() => setAutoShot(null)}
                storyOpen={storyOpen}
                onStoryClose={() => setStoryOpen(false)}
                cookbook={cookbookView}
                onContinue={continueFrom}
                onFrameDirect={async (sub, body) => {
                  setError(null);
                  try {
                    const res = await api("POST", `projects/${projectRef.current.id}/${sub}`, body);
                    await refresh();
                    return res || {};
                  } catch (err) {
                    fail(err);
                    return null;
                  }
                }}
                models={credit?.models}
                onRegenerateKeyframe={(shotId, { refIds, model }) =>
                  runJob(`shots/${shotId}/keyframe`, {
                    ...(refIds?.length ? { refIds } : {}),
                    model,
                  })
                }
                onRefFrame={(takeId, frame) => addRef("frame", { takeId, frame })}
                onRefUpload={uploadRef}
                onRefGenerate={(body) => addRef("generate", body)}
                onRefDelete={(refId) => runJob(`refs/${refId}`, undefined, "DELETE")}
                onRemoveRefKeyframe={(shotId) =>
                  runJob(`shots/${shotId}/keyframe/from-ref`, undefined, "DELETE")
                }
                onRefKeyframe={(shotId, refId) =>
                  runJob(`shots/${shotId}/keyframe/from-ref`, { refId })
                }
                onSaveShot={(shotId, prompt) => {
                  const fields = { shots: [{ id: shotId, prompt }] };
                  mutate(fields, () => fields);
                }}
                audioUrl={audioUrl}
                error={error ? t(`Errors.${error}`) : null}
                onDismissError={() => setError(null)}
              />
            </div>
          )}
          {step === "finish" && renderFinish()}
        </div>

        {error && step !== "edit" && (
          <ErrorAlert text={t(`Errors.${error}`)} onClose={() => setError(null)} />
        )}
        {reused && (
          <div
            role="status"
            className="add-video-modal__notice"
            style={{
              background: "#eff6ff",
              border: "1px solid #bfdbfe",
              borderRadius: "8px",
              padding: "12px 16px",
              margin: "12px 0",
              display: "flex",
              gap: "12px",
              fontSize: "13px",
              lineHeight: "1.5",
              color: "#1e3a8a",
            }}
          >
            <div style={{ flex: 1 }}>{t("Reused")}</div>
            <button
              type="button"
              aria-label={i18n.t("FileModal.APIProviders.VideoGeneration.Close")}
              onClick={() => setReused(false)}
              style={{ background: "transparent", border: "none", fontSize: "18px", color: "#1d4ed8", cursor: "pointer", padding: 0, lineHeight: 1 }}
            >
              ×
            </button>
          </div>
        )}
        <div className="app-modal__footer" style={{ marginTop: 8 }}>
          {step !== "finish" && (
            <CreditEstimate
              btnRef={creditBtnRef}
              loading={creditLoading}
              open={creditOpen && !!credit}
              rows={rows}
              available={Number(credit?.availableCredits) || 0}
              onToggle={toggleCredit}
            />
          )}
          {summary && <span style={{ fontSize: 13, color: "#64748b" }}>{summary}</span>}
          {back && (
            <button
              type="button"
              className="app-modal__btn app-modal__btn--ghost"
              disabled={busy}
              onClick={back.onClick}
            >
              {back.label}
            </button>
          )}
          {extras.map((x) => (
            <button
              key={x.label}
              type="button"
              className="app-modal__btn app-modal__btn--ghost"
              disabled={busy || x.disabled}
              onClick={x.onClick}
            >
              {x.label}
            </button>
          ))}
          {step === "storyboard" && !ldDraft && (
            <ModelPick
              kind="video"
              value={videoModel}
              onChange={setVideoModel}
              models={credit?.models}
            />
          )}
          {next && (
            <button
              type="button"
              className="app-modal__btn app-modal__btn--primary"
              disabled={busy || next.disabled}
              aria-disabled={next.invalid || undefined}
              style={next.invalid ? { opacity: 0.5 } : undefined}
              title={next.title}
              onClick={next.onClick}
            >
              {next.label}
            </button>
          )}
        </div>
      </PreviewHost>
    </div>
  );
}
